import { useRef } from 'react';
import { format, parseISO } from 'date-fns';
import { Paperclip } from '@phosphor-icons/react';
import { getEventCategory, getEventCategoryBgLight } from '../../utils/categories';
import { getCategoryIcon } from '../UI/Icons';
import { getTimeInTimezone } from '../../utils/timezone';
import { useFocusTrap, useEscapeKey } from '../../utils/accessibility';

export default function DayEventsModal({ 
  isOpen, 
  onClose, 
  date,
  events = [],
  onEventClick,
  onAddNew,
  homeTimezone,
  selectedTimezone
}) {
  const modalRef = useRef(null);
  
  // Accessibility: trap focus and handle escape key
  useFocusTrap(isOpen, modalRef);
  useEscapeKey(isOpen, onClose);
  
  if (!isOpen || !date) return null;

  const modalTitle = `Events for ${format(date, 'EEEE, d MMMM yyyy')}`;

  return (
    <div 
      className="fixed inset-0 z-50 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="day-events-modal-title"
    >
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 transition-opacity"
        onClick={onClose}
        aria-hidden="true"
      />
      
      {/* Modal */}
      <div className="relative min-h-screen flex items-center justify-center p-4">
        <div 
          ref={modalRef}
          className="relative rounded-lg shadow-2xl w-full max-w-md animate-scale-in"
          style={{ backgroundColor: 'var(--color-bg-canvas)' }}
        >
          {/* Header */}
          <div 
            className="flex items-center justify-between px-6 py-4"
            style={{ borderBottom: '1px solid var(--color-border-subtle)' }}
          >
            <div>
              <h2 
                id="day-events-modal-title"
                className="text-xl font-display font-semibold" 
                style={{ color: 'var(--color-text-primary)' }}
              >
                {format(date, 'EEEE')}
              </h2>
              <p className="text-sm" style={{ color: 'var(--color-text-muted)' }}>
                {format(date, 'd MMMM yyyy')}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-sm transition-colors"
              style={{ color: 'var(--color-text-muted)' }}
              aria-label="Close modal"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          {/* Events List */}
          <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
            {events.length === 0 ? (
              <div className="text-center py-8">
                <p style={{ color: 'var(--color-text-muted)' }}>No events on this day</p>
              </div>
            ) : (
              <div className="space-y-2">
                {events.map((event, index) => {
                  const category = getEventCategory(event.category);
                  const isFlightOrTransit = event.category === 'flight' || event.category === 'transit';
                  const isAccommodation = event.category === 'accommodation';
                  const categoryStyle = getEventCategoryBgLight(event.category);
                  
                  // Determine which timezone to display in
                  const displayTimezone = selectedTimezone === 'home' ? homeTimezone : selectedTimezone;
                  
                  // Get the appropriate datetime and calculate display time
                  let displayTime = '';
                  let timeLabel = '';
                  
                  if (event.isStart && event.isEnd) {
                    // Single day event
                    displayTime = getTimeInTimezone(event.start_datetime, displayTimezone);
                    timeLabel = displayTime;
                  } else if (event.isStart) {
                    displayTime = getTimeInTimezone(event.start_datetime, displayTimezone);
                    if (isAccommodation) {
                      timeLabel = `Check-in ${displayTime}`;
                    } else if (isFlightOrTransit) {
                      timeLabel = `Departs ${displayTime}`;
                    } else {
                      timeLabel = `Starts ${displayTime}`;
                    }
                  } else if (event.isEnd) {
                    displayTime = getTimeInTimezone(event.end_datetime, displayTimezone);
                    if (isAccommodation) {
                      timeLabel = `Check-out ${displayTime}`;
                    } else if (isFlightOrTransit) {
                      timeLabel = `Arrives ${displayTime}`;
                    } else {
                      timeLabel = `Ends ${displayTime}`;
                    }
                  } else {
                    // Middle day
                    timeLabel = 'All day';
                  }

                  return (
                    <button
                      key={`${event.id}-${index}`}
                      onClick={() => {
                        onEventClick(event);
                        onClose();
                      }}
                      className="w-full text-left p-3 rounded-sm transition-all hover:shadow-md"
                      style={{
                        ...categoryStyle,
                        border: `1px solid ${categoryStyle.color}33`,
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-shrink-0 mt-0.5">
                          {getCategoryIcon(event.category, { size: 20 })}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-sm truncate flex items-center gap-1">
                            {event.title}
                            {event.attachment_count > 0 && (
                              <Paperclip size={12} className="flex-shrink-0 opacity-60" />
                            )}
                          </div>
                          <div className="text-xs opacity-75 mt-0.5">
                            {timeLabel}
                          </div>
                          {event.location && (
                            <div className="text-xs opacity-60 mt-0.5 truncate">
                              {event.location}
                            </div>
                          )}
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Footer */}
          <div 
            className="px-6 py-4 rounded-b-lg"
            style={{ 
              borderTop: '1px solid var(--color-border-subtle)',
              backgroundColor: 'var(--color-bg-surface)',
            }}
          >
            <button
              onClick={() => {
                onAddNew();
                onClose();
              }}
              className="btn-primary w-full"
            >
              <PlusIcon className="w-4 h-4" />
              Add Entry
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function XIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  );
}

function PlusIcon({ className }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
    </svg>
  );
}
